<script setup>
import chroma from 'chroma-js';
import { useCommonImports } from '~/common/composables/common-imports.composable';

const props = defineProps({
  form: {
    type: Object,
    required: true,
  },
  hideColorPicker: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['updateColorRange', 'updateColor']);

const { $t } = useCommonImports();

function getColorRangeWidth() {
  const width = window.innerWidth;
  return Math.floor(width / 4) - 80;
}

function getFormColors() {
  const data = chroma.scale(['#ffffff', props.form.color]).mode('lch').colors(props.form.color_range);
  return data;
}

function updateColorRange(type) {
  let new_value;
  if (type === 'increment') {
    new_value = Number(props.form.color_range || 5) + 1;
  }
  else {
    new_value = Number(props.form.color_range || 5) - 1;
  }
  emit('updateColorRange', new_value);
}

function handleColorSelect(color) {
  emit('updateColor', color);
}
</script>

<template>
  <div class="space-y-4">
    <template v-if="!hideColorPicker">
      <div class="text-sm font-semibold text-gray-900 mb-2">
        {{ $t('Colors') }}
      </div>

      <div class="flex items-center gap-2">
        <HawkButton
          icon
          type="plain"
          :disabled="form.color_range <= 2"
          @click="updateColorRange('decrement')"
        >
          <IconHawkMinusCircle />
        </HawkButton>

        <BiColorPicker
          :active-color="form.color"
          class="pt-1"
          @color-selected="handleColorSelect"
        >
          <template #trigger>
            <div
              class="flex rounded-md overflow-hidden border border-gray-200"
              :style="{ width: `${getColorRangeWidth() - 20}px` }"
            >
              <div
                v-for="color in getFormColors()"
                :key="color"
                class="h-8"
                :style="{
                  backgroundColor: color,
                  width: `${Math.floor(getColorRangeWidth() / getFormColors().length)}px`,
                }"
              />
            </div>
          </template>
        </BiColorPicker>

        <HawkButton icon type="plain" @click="updateColorRange('increment')">
          <IconHawkPlusCircle />
        </HawkButton>
      </div>
    </template>
    <div>
      <div class="text-sm font-semibold text-gray-900 mb-2">
        {{ $t('Start the range at') }}
      </div>
      <RadiogroupElement
        name="start_range_at"
        class="mb-1"
        :items="[
          { value: 'min', label: $t('Smallest value in this column') },
          { value: 'custom', label: $t('Custom value') },
        ]"
        default="min"
      />
      <TextElement
        v-if="form.start_range_at === 'custom'"
        name="start_range_value"
        :rules="['required', 'numeric']"
        :default="0"
      />
    </div>

    <div>
      <div class="text-sm font-semibold text-gray-900 mb-2">
        {{ $t('End the range at') }}
      </div>
      <RadiogroupElement
        name="end_range_at"
        class="mb-1"
        :items="[
          { value: 'max', label: $t('Largest value in this column') },
          { value: 'custom', label: $t('Custom value') },
        ]"
        default="max"
      />
      <TextElement
        v-if="form.end_range_at === 'custom'"
        name="end_range_value"
        :rules="['required', 'numeric']"
        :default="(form.min ?? 0) + 1"
      />
    </div>
  </div>
</template>
